import React, { useCallback, useEffect, useRef, useState } from "react";
import { Map, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import styled from "@emotion/styled";
import { AutoComplete, Input, Spin, Tag } from "antd";
import { EnvironmentFilled, EnvironmentOutlined, LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import "leaflet/dist/leaflet.css";
import COLORS from "common/Colors";
import { reverseGeocode, matchCountryName, searchLocations } from "utils/geocode";

// A brand-colored pin (self-contained SVG, no external icon assets) so the
// marker matches the app's palette instead of Leaflet's default red pin.
const MARKER_ICON = L.divIcon({
  className: "location-map-picker-marker",
  html: `
    <svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="17" cy="31" rx="6" ry="2" fill="rgba(0,0,0,0.18)" />
      <path
        d="M17 2C10.37 2 5 7.37 5 14c0 9 12 18 12 18s12-9 12-18c0-6.63-5.37-12-12-12z"
        fill="${COLORS.BLUE}"
        stroke="#ffffff"
        stroke-width="1.5"
      />
      <circle cx="17" cy="14" r="5" fill="#ffffff" />
    </svg>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 32],
  popupAnchor: [0, -30],
});

const Card = styled.div`
  margin-bottom: 24px;
  border: 1px solid #e8e8ef;
  border-radius: 10px;
  overflow: hidden;
  background: ${COLORS.WHITE};
  box-shadow: 0 1px 4px rgba(19, 19, 19, 0.06);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: ${COLORS.SIDEBAR};
  border-bottom: 1px solid #e8e8ef;
`;

const HeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 13px;
  color: ${COLORS.SECONDARY_BLACK};

  .anticon {
    color: ${COLORS.BLUE};
    font-size: 15px;
  }
`;

const CoordTag = styled(Tag)`
  &.ant-tag {
    margin: 0;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${COLORS.TEXT.PRIMARY};
    background: ${COLORS.WHITE};
    border-color: #d9d9e3;
  }
`;

const SearchRow = styled.div`
  padding: 10px 14px;
  background: ${COLORS.WHITE};
  border-bottom: 1px solid #e8e8ef;

  .ant-select {
    width: 100%;
  }
`;

const MapArea = styled.div`
  position: relative;

  .leaflet-container {
    height: 300px;
    width: 100%;
    cursor: crosshair;
  }

  .leaflet-control-attribution {
    font-size: 10px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  pointer-events: none;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 12px;
  color: ${COLORS.TEXT.PRIMARY};
  background: ${COLORS.WHITE};
  border-top: 1px solid #f0f0f4;

  .anticon {
    color: #b0b0bd;
  }
`;

const spinnerIcon = <LoadingOutlined style={{ fontSize: 22, color: COLORS.BLUE }} spin />;

const DEFAULT_CENTER = [20, 0];
const DEFAULT_ZOOM = 2;
const SELECTED_ZOOM = 14;

const toFloat = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Reusable click/drag map picker for the Add/Edit Location forms. Reports
// the picked point, its reverse-geocoded name, and a country matched
// against the caller's configured country list back to the caller; the
// caller owns writing those values into its form.
const LocationMapPicker = ({
  lat,
  lng,
  countryOptions = [],
  onLocationChange,
  disabled,
  autoLocate,
}) => {
  const [position, setPosition] = useState(() => {
    const initialLat = toFloat(lat);
    const initialLng = toFloat(lng);
    return initialLat != null && initialLng != null ? [initialLat, initialLng] : null;
  });
  const [resolving, setResolving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const hasAutoLocated = useRef(false);
  const mapRef = useRef(null);
  const mapAreaRef = useRef(null);
  const searchTimer = useRef(null);

  // The map is mounted inside an antd Modal, which animates its width/height
  // open; Leaflet measures the container once on init and won't notice the
  // later resize on its own, leaving it rendered at the wrong (tiny) size.
  // Re-measure whenever the container's actual size changes.
  useEffect(() => {
    const container = mapAreaRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const invalidateSize = () => {
      mapRef.current?.leafletElement?.invalidateSize();
    };

    const observer = new ResizeObserver(invalidateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const nextLat = toFloat(lat);
    const nextLng = toFloat(lng);
    if (nextLat == null || nextLng == null) return;
    setPosition((prev) =>
      prev && prev[0] === nextLat && prev[1] === nextLng ? prev : [nextLat, nextLng]
    );
  }, [lat, lng]);

  const selectPosition = useCallback(
    (nextLat, nextLng, picked = false) => {
      const roundedLat = Number(nextLat.toFixed(6));
      const roundedLng = Number(nextLng.toFixed(6));
      setPosition([roundedLat, roundedLng]);
      setResolving(true);
      reverseGeocode(roundedLat, roundedLng)
        .then((details) => {
          onLocationChange({
            latitude: roundedLat,
            longitude: roundedLng,
            locationName: details.locationName,
            country: matchCountryName(details.country, countryOptions) || "",
            picked,
          });
        })
        .catch(() => {
          onLocationChange({
            latitude: roundedLat,
            longitude: roundedLng,
            locationName: "",
            country: "",
            picked,
          });
        })
        .finally(() => setResolving(false));
    },
    [countryOptions, onLocationChange]
  );

  // Default the map to the user's current position (once) when no saved
  // coordinates exist yet, e.g. when opening a fresh Add Location form.
  useEffect(() => {
    if (!autoLocate || disabled || hasAutoLocated.current || position) return;
    if (!navigator.geolocation) return;

    hasAutoLocated.current = true;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (geoPosition) => {
        setLocating(false);
        selectPosition(geoPosition.coords.latitude, geoPosition.coords.longitude);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLocate, disabled, position]);

  const handleSearch = (value) => {
    setSearchValue(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!value || value.trim().length < 3) {
      setSearchOptions([]);
      return;
    }

    searchTimer.current = setTimeout(() => {
      setSearching(true);
      searchLocations(value)
        .then((results) => {
          setSearchOptions(
            results.map((result, index) => ({
              value: `${result.displayName}__${index}`,
              label: result.displayName,
              result,
            }))
          );
        })
        .finally(() => setSearching(false));
    }, 400);
  };

  const handleSearchSelect = (_, option) => {
    const result = option?.result;
    if (!result) return;
    const roundedLat = Number(result.lat.toFixed(6));
    const roundedLng = Number(result.lng.toFixed(6));
    setPosition([roundedLat, roundedLng]);
    setSearchValue(result.locationName || result.displayName);
    onLocationChange({
      latitude: roundedLat,
      longitude: roundedLng,
      locationName: result.locationName,
      country: matchCountryName(result.country, countryOptions) || "",
      // Not `picked`: selecting from the search dropdown should move the
      // map but leave the modal open so the user can fine-tune the pin.
      picked: false,
    });
  };

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const handleMapClick = (event) => {
    if (disabled) return;
    selectPosition(event.latlng.lat, event.latlng.lng, true);
  };

  const handleMarkerDragEnd = (event) => {
    if (disabled) return;
    const dragged = event.target.getLatLng();
    selectPosition(dragged.lat, dragged.lng, true);
  };

  return (
    <Card>
      <CardHeader>
        <HeaderTitle>
          <EnvironmentOutlined />
          Select Location on Map
        </HeaderTitle>
        <CoordTag>
          {position ? `${position[0].toFixed(6)}, ${position[1].toFixed(6)}` : "No location selected"}
        </CoordTag>
      </CardHeader>

      <SearchRow>
        <AutoComplete
          value={searchValue}
          options={searchOptions}
          onSearch={handleSearch}
          onSelect={handleSearchSelect}
          onChange={setSearchValue}
          disabled={disabled}
          notFoundContent={
            searching ? "Searching…" : searchValue.trim().length >= 3 ? "No matching location found" : null
          }
        >
          <Input prefix={<SearchOutlined />} allowClear placeholder="Search for a location by name…" />
        </AutoComplete>
      </SearchRow>

      <MapArea ref={mapAreaRef}>
        <Map
          ref={mapRef}
          center={position || DEFAULT_CENTER}
          zoom={position ? SELECTED_ZOOM : DEFAULT_ZOOM}
          onClick={handleMapClick}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {position && (
            <Marker
              position={position}
              icon={MARKER_ICON}
              draggable={!disabled}
              onDragend={handleMarkerDragEnd}
            />
          )}
        </Map>
        {(resolving || locating) && (
          <LoadingOverlay>
            <Spin indicator={spinnerIcon} />
          </LoadingOverlay>
        )}
      </MapArea>

      <CardFooter>
        <EnvironmentFilled />
        {locating
          ? "Getting your current location…"
          : disabled
          ? "Map is read-only."
          : "Click anywhere on the map or drag the pin to set the exact location."}
      </CardFooter>
    </Card>
  );
};

export default LocationMapPicker;
